import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Round EPO-Final — company-aware Purchase Order branding. ONE centralized
// legal-entity config (logo, legal name, colors, Ship-To, contact block)
// drives the PDF; no `if company === ...` scattered through the builder.
// The owning company comes from the EPO header's owning-company field
// (pending schema approval — until then every EPO resolves to the default
// S.S. White Technologies, preserving the historical identity). The
// Vendor/Payee block (header.company) is untouched.
import { resolveEpoCompanyBrand, loadBrandLogo } from "./epoCompanyBranding";

const fmtMoney = (n) => {
    if (n === null || n === undefined || n === "") return "$0.00";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const fmtDate = (v) => {
    if (!v) return "-";
    return new Date(v).toLocaleDateString("en-US");
};

// ── Shared builder — returns the jsPDF doc object ────────────
const buildEpoDoc = async (epoNumber, apiInstance) => {

    const res = await apiInstance.get(`Epo/${epoNumber}`);
    const payload = res.data;
    const header = payload?.header ?? payload?.Header ?? {};

    // Owning company / legal entity branding (buyer). Defaults to
    // S.S. White for EPOs with no owning-company value (legacy rows).
    const brand = resolveEpoCompanyBrand(header);
    const brandLogo = await loadBrandLogo(brand.logo);   // null → text fallback

    const lines =
        Array.isArray(payload?.lines) && payload.lines.length > 0
            ? payload.lines
            : Array.isArray(payload?.Lines) && payload.Lines.length > 0
                ? payload.Lines
                : Array.isArray(header?.lines)
                    ? header.lines
                    : [];

    // Round EPO-Final hotfix (owner PDF report, #85967) — GET Epo/{n} has
    // NEVER carried an approvals array ({viewerCan..., header, lines,
    // attachments}), so this was always [] and EVERY approved PDF fell into
    // the "empty = auto-approved" fallback, printing "<creator>
    // (Auto-Approved)" even when a manager approved it. Fetch the real
    // approval history like the timeline/modal do.
    let approvals = [];
    try {
        const hist = await apiInstance.get(`epo-approvals/${epoNumber}/history`);
        const hd = hist?.data;
        approvals = Array.isArray(hd) ? hd
            : Array.isArray(hd?.items) ? hd.items
            : Array.isArray(hd?.Items) ? hd.Items
            : [];
    } catch (e) {
        console.warn(`epoToPdf: approval history unavailable for #${epoNumber}`, e);
    }

    // ── Created By ───────────────────────────────────────────
    let createdByName = String(header.createdBy || "");
    try {
        const r = await apiInstance.get(`OrgChart/${header.createdBy}`);
        const e = r.data;
        const name = `${e?.firstName ?? ""} ${e?.lastName ?? ""}`.trim();
        if (name) createdByName = `${name} ${header.createdBy}`;
    } catch { }

    // ── Approved By ──────────────────────────────────────────
    let approvedByText = "";
    const status = String(header.status || "").toUpperCase();

    let approvalStampText = "";
    if (status === "APPROVED") {
        // FINAL approver = the last APPROVED audit row by step order; the
        // "(Auto-Approved)" tag comes from that row's own comment — never
        // from a missing history (the old bug mislabeled manager approvals).
        const stepOf = (a) => Number(a.stepNumber ?? a.StepNumber ?? 0);
        const approvedRow = approvals
            .filter(a => String(a.status ?? a.Status ?? "").toUpperCase() === "APPROVED")
            .sort((a, b) => stepOf(a) - stepOf(b))
            .pop();

        if (approvedRow) {
            const approverId =
                approvedRow.actorEmpId
                ?? approvedRow.ActorEmpId
                ?? approvedRow.approverEmpId
                ?? approvedRow.ApproverEmpId
                ?? approvedRow.empId
                ?? approvedRow.empid
                ?? approvedRow.EmpId;
            const isAuto = /^auto-approved/i.test(String(approvedRow.comment ?? approvedRow.Comment ?? ""));

            try {
                const r = await apiInstance.get(`OrgChart/${approverId}`);
                const e = r.data;
                const name = `${e?.firstName ?? ""} ${e?.lastName ?? ""}`.trim();
                approvedByText = (name ? `${name} ${approverId}` : String(approverId))
                    + (isAuto ? " (Auto-Approved)" : "");
            } catch {
                approvedByText = String(approverId || "-") + (isAuto ? " (Auto-Approved)" : "");
            }

            const stampRaw = approvedRow.stamp ?? approvedRow.Stamp;
            if (stampRaw) {
                const d = new Date(stampRaw);
                if (!Number.isNaN(d.getTime())) approvalStampText = d.toLocaleDateString("en-US");
            }
        } else {
            // History endpoint unreachable — best-effort legacy fallback.
            approvedByText = `${createdByName} (Auto-Approved)`;
        }

    } else if (status.includes("PENDING") || status.includes("SUBMITTED")) {
        const nextId = header.nextApprover ?? header.NextApprover;
        if (nextId) {
            try {
                const r = await apiInstance.get(`OrgChart/${nextId}`);
                const e = r.data;
                const name = `${e?.firstName ?? ""} ${e?.lastName ?? ""}`.trim();
                approvedByText = name
                    ? `Pending - ${name} (${nextId})`
                    : `Pending - ${nextId}`;
            } catch {
                approvedByText = `Pending - ${nextId}`;
            }
        } else {
            approvedByText = "Pending Approval";
        }

    } else if (status.includes("REJECT")) {
        approvedByText = "Rejected";

    } else if (status.includes("VOID")) {
        approvedByText = "Void";

    } else {
        approvedByText = status || "-";
    }

    // ── Totals ───────────────────────────────────────────────
    const subtotal = lines.reduce((sum, ln) => sum + (Number(ln.extendedCost) || 0), 0);
    const taxRate = Number(header.taxRate) || 0;
    const totalTax = subtotal * (taxRate / 100);
    const shipping = Number(header.shippingCharges) || 0;
    const miles = Number(header.milesTraveled) || 0;
    const mileRate = Number(header.mileageRate) || 0;
    const mileage = miles * mileRate;
    const grandTotal = subtotal + totalTax + shipping + mileage;

    // ── jsPDF setup ──────────────────────────────────────────
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 48;                 // page margin
    const contentW = pageW - M * 2;
    const INK = 33, MUTED = 110;  // grayscale tones
    const BRAND = brand.colors.primary;
    let y = M;

    const label = (t, x, yy, opts = {}) => {
        doc.setFont("helvetica", "bold").setFontSize(opts.size || 8).setTextColor(MUTED);
        doc.text(String(t).toUpperCase(), x, yy, opts);
    };
    const value = (t, x, yy, opts = {}) => {
        doc.setFont("helvetica", opts.bold ? "bold" : "normal").setFontSize(opts.size || 9).setTextColor(INK);
        doc.text(t == null || t === "" ? "-" : String(t), x, yy, opts);
    };

    // ── Header band: OWNING COMPANY identity (left) + PURCHASE ORDER (right)
    // The left block is the EPO's legal entity (buyer) — logo when the
    // approved asset loads, typeset brand fallback otherwise. Address +
    // contact lines come from the same central brand config.
    let leftBottom = y;
    if (brandLogo) {
        const logoH = brand.logo.height || 32;
        const logoW = logoH * (brandLogo.w / brandLogo.h);
        doc.addImage(brandLogo.dataUrl, "PNG", M, y, logoW, logoH);
        // Optional typeset lockup beside an icon-only mark (SSS: blue
        // "SHUKLA" over dark "SURGICAL SUPPORT", mirroring the brand's own
        // invoice lockup — the approved icon asset plus typography).
        if (Array.isArray(brand.logoTextLockup) && brand.logoTextLockup.length) {
            let ty = y + 13;
            brand.logoTextLockup.forEach((lnDef) => {
                doc.setFont("helvetica", lnDef.bold ? "bold" : "normal")
                    .setFontSize(lnDef.size)
                    .setTextColor(...lnDef.color);
                doc.text(lnDef.text, M + logoW + 10, ty);
                ty += lnDef.size + 2;
            });
        }
        leftBottom = y + logoH;
    } else {
        // Text fallback in brand colors — PDF generation never depends on
        // the image loading.
        let ty = y + 6;
        brand.headerLines.forEach((lnDef) => {
            doc.setFont("helvetica", lnDef.bold ? "bold" : "normal")
                .setFontSize(lnDef.size)
                .setTextColor(...lnDef.color);
            doc.text(lnDef.text, M, ty);
            ty += lnDef.size + 3;
        });
        leftBottom = ty - 6;
    }
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(MUTED);
    let infoY = leftBottom + 12;
    [...brand.addressLines, ...brand.contactLines].forEach((lnText) => {
        doc.text(lnText, M, infoY);
        infoY += 10;
    });
    leftBottom = infoY - 10;

    doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(INK);
    doc.text("PURCHASE ORDER", pageW - M, y + 8, { align: "right" });
    doc.setFontSize(11).setTextColor(MUTED);
    doc.text(`No. ${epoNumber}`, pageW - M, y + 26, { align: "right" });
    doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(MUTED);
    doc.text(
        "All invoices, packages, correspondence, shipping papers &\nbills of lading must reference PO number and line item.",
        pageW - M, y + 40, { align: "right" }
    );

    y = Math.max(leftBottom, y + 52) + 12;
    // divider rule under the header band — brand primary color
    doc.setDrawColor(...BRAND).setLineWidth(1.2);
    doc.line(M, y, pageW - M, y);
    y += 22;

    // ── Vendor & Ship-To: two aligned columns ────────────────
    // Vendor/Payee = EpoHeader vendor data (unchanged). Ship To = the EPO's
    // OWNING legal entity from the brand config.
    const colGap = 24;
    const colW = (contentW - colGap) / 2;
    const leftX = M;
    const rightX = M + colW + colGap;
    const boxTop = y;

    const vendorLines = [
        header.company || "",
        header.street1 || "",
        header.street2 || "",
        [header.city, header.state, header.zip].filter(Boolean).join(", "),
        header.country || "",
    ].filter(Boolean);

    const firstTwoName = createdByName.split(" ").slice(0, 2).join(" ");
    const shipLines = [
        brand.shipToName,
        `ATTN: ${firstTwoName} #${epoNumber}`,
        ...brand.shipToAddressLines,
    ];

    label("Vendor", leftX, y);
    label("Ship To", rightX, y);
    y += 14;
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(INK);
    const lineH = 12;
    vendorLines.forEach((ln, i) => doc.text(ln, leftX, y + i * lineH));
    shipLines.forEach((ln, i) => doc.text(ln, rightX, y + i * lineH));
    y = boxTop + 14 + Math.max(vendorLines.length, shipLines.length) * lineH + 16;

    // ── Meta strip: Order Date / Vendor ID / Terms ───────────
    const metaY = y;
    const m1 = M, m2 = M + contentW * 0.37, m3 = M + contentW * 0.70;
    label("Order Date", m1, metaY);
    label("Vendor ID", m2, metaY);
    label("Terms", m3, metaY);
    value(fmtDate(header.createdDate), m1, metaY + 13);
    value(header.vendorId, m2, metaY + 13);
    value(header.paymentType, m3, metaY + 13);
    y = metaY + 30;

    label("Purpose", m1, y);
    value(header.epoRemark, m1 + 56, y);
    y += 22;

    // ── Line items table ─────────────────────────────────────
    autoTable(doc, {
        startY: y,
        margin: { left: M, right: M },
        head: [[
            "Item", "Qty", "U/M", "Description",
            "Acct (Grp/Dept/Acct)", "Unit Price", "Disc", "Tax", "Ext. Cost"
        ]],
        body: lines.map((ln, idx) => [
            ln.lineNum ?? idx + 1,
            ln.quan ?? "-",
            ln.uom ?? "EA",
            ln.description || "-",
            [ln.productGroup, ln.dept, ln.acct]
                .map(v => (v == null || v === "") ? "-" : String(v))
                .join(" / "),
            fmtMoney(ln.unitPrice),
            ln.discountPercent ? `${ln.discountPercent}%` : "-",
            ln.taxPercent ? `${ln.taxPercent}%` : (ln.tax ? "Y" : "-"),
            fmtMoney(ln.extendedCost),
        ]),
        theme: "grid",
        styles: { fontSize: 8.5, cellPadding: 5, textColor: INK, lineColor: [210, 210, 210], lineWidth: 0.5, valign: "middle" },
        headStyles: {
            // table header uses the owning company's brand color
            fillColor: brand.colors.tableHead, textColor: 255, fontStyle: "bold",
            fontSize: 8, halign: "left", lineWidth: 0,
        },
        alternateRowStyles: { fillColor: [247, 247, 250] },
        columnStyles: {
            0: { cellWidth: 30, halign: "center" },
            1: { cellWidth: 34, halign: "center" },
            2: { cellWidth: 34, halign: "center" },
            3: { cellWidth: "auto" },
            4: { cellWidth: 110 },
            5: { cellWidth: 62, halign: "right" },
            6: { cellWidth: 34, halign: "center" },
            7: { cellWidth: 34, halign: "center" },
            8: { cellWidth: 66, halign: "right" },
        },
    });

    y = doc.lastAutoTable.finalY + 20;

    // ── Notes (left) ─────────────────────────────────────────
    const notesTop = y;
    const writeNote = (lbl, text) => {
        if (!text) return;
        label(lbl, M, y);
        doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(INK);
        const wrapped = doc.splitTextToSize(String(text), contentW * 0.52);
        doc.text(wrapped, M, y + 12);
        y += 12 + wrapped.length * 11 + 8;
    };
    writeNote("Purchase Notes", header.noteExternal);
    writeNote("Internal Notes", header.noteInternal);

    // ── Totals block (right), aligned to notesTop ────────────
    const totalsRows = [
        ["Subtotal", fmtMoney(subtotal)],
        ...(totalTax > 0 ? [["Total Tax", fmtMoney(totalTax)]] : []),
        ["Shipping", fmtMoney(shipping)],
        ["Mileage Allowance", fmtMoney(mileage)],
    ];
    const tW = 230;
    const tX = pageW - M - tW;     // left edge of totals block
    const tLabelX = tX + 8;
    const tValX = pageW - M - 8;
    let ty = notesTop;
    doc.setFontSize(9);
    totalsRows.forEach(([lbl, val]) => {
        doc.setFont("helvetica", "normal").setTextColor(MUTED);
        doc.text(lbl, tLabelX, ty, { align: "left" });
        doc.setTextColor(INK);
        doc.text(val, tValX, ty, { align: "right" });
        ty += 15;
    });
    // grand total bar — owning company brand color (+ accent rule only when
    // the actual brand carries one, e.g. SSS orange)
    if (brand.colors.accent) {
        doc.setFillColor(...brand.colors.accent);
        doc.rect(tX, ty - 5, tW, 3, "F");
    }
    doc.setFillColor(...brand.colors.totalBar);
    doc.rect(tX, ty - 2, tW, 22, "F");
    doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(255);
    doc.text("GRAND TOTAL", tLabelX, ty + 13, { align: "left" });
    doc.text(fmtMoney(grandTotal), tValX, ty + 13, { align: "right" });
    const totalsBottom = ty + 28;

    y = Math.max(y, totalsBottom) + 20;

    // ── Signature footer ─────────────────────────────────────
    const footTop = Math.min(Math.max(y, pageH - 96), pageH - 96);
    doc.setDrawColor(210).setLineWidth(0.5);
    doc.line(M, footTop, pageW - M, footTop);
    const fY = footTop + 18;
    label("Created By", M, fY);
    value(createdByName, M, fY + 13, { bold: true });
    label("Approved By", M + contentW * 0.5, fY);
    value(approvedByText, M + contentW * 0.5, fY + 13, { bold: true });

    // Owner request (2026-08-20, #85967) — state on the PO itself that the
    // approval is electronic, so nobody hunts for an ink signature.
    if (status === "APPROVED") {
        doc.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(MUTED);
        const eSign = approvalStampText
            ? `This purchase order was approved electronically via the S.S. White Dashboard on ${approvalStampText} and is valid without a physical signature.`
            : "This purchase order was approved electronically via the S.S. White Dashboard and is valid without a physical signature.";
        doc.text(eSign, M, fY + 34, { maxWidth: contentW });
    }

    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(MUTED);
    doc.text("Page 1 of 1", pageW - M, pageH - 24, { align: "right" });

    return doc;
};

// ── Export 1: Download PDF directly ─────────────────────────
export const downloadEpoPdf = async (epoNumber, apiInstance) => {
    const doc = await buildEpoDoc(epoNumber, apiInstance);
    doc.save(`EPO-${epoNumber}.pdf`);
};

// ── Export 2: Get base64 string for email attachment ─────────
export const getEpoPdfBase64 = async (epoNumber, apiInstance) => {
    const doc = await buildEpoDoc(epoNumber, apiInstance);
    return doc.output("datauristring").split(",")[1]; // pure base64
};
